import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { vehicleService } from '../../services/vehicleService';
import { carrierService } from '../../services/carrierService';

const VehicleSchema = Yup.object().shape({
  plateNumber: Yup.string().required('Plaka numarası zorunludur'),
  vehicleTypeId: Yup.number().required('Araç tipi zorunludur'),
  carrierId: Yup.number().required('Taşıyıcı zorunludur'),
  driverName: Yup.string(),
  driverPhone: Yup.string(),
  isActive: Yup.boolean()
});

const VehicleCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  
  // URL'den carrierId parametresini al
  const queryParams = new URLSearchParams(location.search);
  const preselectedCarrierId = queryParams.get('carrierId');

  useEffect(() => {
    fetchCarriers();
    fetchVehicleTypes();
  }, []);

  const fetchCarriers = async () => {
    try {
      const response = await carrierService.getCarriers({ isActive: true });
      if (response && response.success && response.data && response.data.rows) {
        setCarriers(response.data.rows);
      }
    } catch (error) {
      toast.error('Taşıyıcılar yüklenirken bir hata oluştu');
      console.error('Error fetching carriers:', error);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response && response.success && response.data) {
        setVehicleTypes(response.data.vehicleTypes);
      }
    } catch (error) {
      toast.error('Araç tipleri yüklenirken bir hata oluştu');
      console.error('Error fetching vehicle types:', error);
    }
  };

  const initialValues = {
    plateNumber: '',
    vehicleTypeId: '',
    carrierId: preselectedCarrierId || '',
    driverName: '',
    driverPhone: '',
    isActive: true
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await vehicleService.createVehicle(values);
      toast.success('Araç başarıyla oluşturuldu');
      navigate('/vehicles');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Araç oluşturulurken bir hata oluştu');
      console.error('Error creating vehicle:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900">Yeni Araç Ekle</h1>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <Formik
          initialValues={initialValues}
          validationSchema={VehicleSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="plateNumber" className="block text-sm font-medium text-gray-700">
                    Plaka Numarası *
                  </label>
                  <div className="mt-1">
                    <Field
                      type="text"
                      name="plateNumber"
                      id="plateNumber"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.plateNumber && touched.plateNumber ? 'border-red-500' : ''
                      }`}
                    />
                    <ErrorMessage name="plateNumber" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="vehicleTypeId" className="block text-sm font-medium text-gray-700">
                    Araç Tipi *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="vehicleTypeId"
                      id="vehicleTypeId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.vehicleTypeId && touched.vehicleTypeId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {vehicleTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name} (Kapasite: {type.capacity})</option>
                      ))}
                    </Field>
                    <ErrorMessage name="vehicleTypeId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="carrierId" className="block text-sm font-medium text-gray-700">
                    Taşıyıcı *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="carrierId"
                      id="carrierId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.carrierId && touched.carrierId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {carriers.map(carrier => (
                        <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                      ))}
                    </Field>
                    <ErrorMessage name="carrierId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="isActive" className="block text-sm font-medium text-gray-700">
                    Durum
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="isActive"
                      id="isActive"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value={true}>Aktif</option>
                      <option value={false}>Pasif</option>
                    </Field>
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="driverName" className="block text-sm font-medium text-gray-700">
                    Sürücü Adı
                  </label>
                  <div className="mt-1">
                    <Field
                      type="text"
                      name="driverName"
                      id="driverName"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="driverPhone" className="block text-sm font-medium text-gray-700">
                    Sürücü Telefonu
                  </label>
                  <div className="mt-1">
                    <Field
                      type="text"
                      name="driverPhone"
                      id="driverPhone"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/vehicles')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default VehicleCreate;
